import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LOGO_URL } from "@/lib/content";

const sections = [
  {
    n: "1",
    title: "Introduction",
    body: [
      'Welcome to Swingle ("Swingle", "we", "our", or "us").',
      "Swingle is an all-in-one platform that enables late-night venues, bars, clubs, event organisers and hospitality businesses (\"Venues\") to sell digital memberships, sell digital event tickets and communicate with their customers through email marketing.",
      'We are committed to protecting your privacy and handling personal information responsibly, securely and in accordance with applicable data protection laws, including the UK General Data Protection Regulation ("UK GDPR") and the Data Protection Act 2018.',
      "This Privacy Policy explains how we collect, use, store and protect personal information when you use Swingle, create a Venue account, or purchase tickets or memberships through a Venue using our platform.",
    ],
  },
  {
    n: "2",
    title: "Who We Are",
    body: [
      "Swingle is a software platform that provides Venues with tools to:",
      {
        list: [
          "Sell digital tickets",
          "Sell digital memberships",
          "Manage customer databases",
          "Send email marketing campaigns",
          "Manage customer communications",
          "Process payments through integrated payment providers",
        ],
      },
      "Depending on how personal information is used, Swingle may act as either a Data Controller or a Data Processor.",
    ],
  },
  {
    n: "3",
    title: "Our Role in Processing Personal Data",
    body: [
      { subheading: "Venue Customer Information" },
      "In most circumstances, the Venue using Swingle is the Data Controller of its customers' personal information.",
      "This means the Venue decides:",
      {
        list: [
          "what personal information is collected;",
          "why it is collected;",
          "how it is used; and",
          "how long it is retained.",
        ],
      },
      "Swingle acts as a Data Processor, processing customer information solely to provide the services requested by the Venue, including:",
      {
        list: [
          "delivering digital tickets;",
          "issuing digital memberships;",
          "storing customer records;",
          "processing transactions;",
          "sending transactional emails; and",
          "sending marketing communications on behalf of the Venue.",
        ],
      },
      "Swingle will only process customer information in accordance with the instructions of the relevant Venue, except where required to comply with applicable laws, prevent fraud, protect platform security or exercise our legal rights.",
      "For questions about how a Venue uses your personal information, please refer to that Venue's own Privacy Policy.",
      { subheading: "Venue Account Information" },
      "Where Venues create an account with Swingle, we act as the Data Controller for the personal information relating to the operation of that account.",
    ],
  },
  {
    n: "4",
    title: "Information We Collect",
    body: [
      { subheading: "Information We Collect From Venues" },
      "When a Venue registers with Swingle, we may collect:",
      {
        list: [
          "Business name",
          "Trading name",
          "Contact name",
          "Email address",
          "Telephone number",
          "Business address",
          "Billing information",
          "Payment details",
          "Login credentials",
          "Account preferences",
        ],
      },
      { subheading: "Information We Process About Venue Customers" },
      "When a customer purchases a ticket or membership through Swingle, we may process information including:",
      {
        list: [
          "Full name",
          "Email address",
          "Telephone number (where required)",
          "Billing details",
          "Purchase history",
          "Membership information",
          "Ticket information",
          "Event attendance records",
          "IP address",
          "Browser and device information",
        ],
      },
      "Payment card information is processed securely by our payment providers and is not stored by Swingle.",
    ],
  },
  {
    n: "5",
    title: "How We Use Personal Information",
    body: [
      "We use personal information to:",
      {
        list: [
          "Create and manage Venue accounts",
          "Deliver digital tickets",
          "Deliver digital memberships",
          "Process payments",
          "Send purchase confirmations",
          "Send transactional emails",
          "Enable Venues to manage their customers",
          "Allow Venues to send email marketing campaigns",
          "Provide customer support",
          "Improve our services",
          "Detect and prevent fraud",
          "Maintain platform security",
          "Comply with legal obligations",
        ],
      },
    ],
  },
  {
    n: "6",
    title: "Lawful Basis for Processing",
    body: [
      "Where Swingle acts as a Data Controller, we rely upon one or more of the following lawful bases:",
      { subheading: "Performance of a Contract" },
      "To:",
      {
        list: [
          "provide the Swingle platform;",
          "process purchases;",
          "issue tickets;",
          "issue memberships;",
          "manage Venue accounts; and",
          "provide customer support.",
        ],
      },
      { subheading: "Legal Obligation" },
      "Where we must retain or disclose information to comply with legal or regulatory requirements.",
      { subheading: "Legitimate Interests" },
      "To:",
      {
        list: [
          "improve the platform;",
          "maintain security;",
          "detect fraud;",
          "investigate misuse;",
          "develop new features; and",
          "protect our legal rights.",
        ],
      },
      { subheading: "Consent" },
      "Where required by law, including certain cookies and communications.",
      "Where Swingle processes personal information on behalf of a Venue, the Venue is responsible for determining the lawful basis for processing that information.",
    ],
  },
  {
    n: "7",
    title: "Marketing Communications",
    body: [
      "Swingle provides email marketing tools that allow Venues to communicate with their customers.",
      "Marketing emails are sent by or on behalf of the relevant Venue.",
      "Each Venue is responsible for ensuring that it has an appropriate lawful basis for sending marketing communications, including obtaining consent where required by applicable law.",
      "Recipients may unsubscribe from marketing emails at any time by using the unsubscribe link contained within each marketing email.",
      "Marketing emails sent through Swingle may include standard tracking technologies, such as open tracking and link tracking, to enable Venues to measure the performance of their email campaigns. This information may include whether an email has been opened, whether links have been clicked, and whether delivery was successful.",
    ],
  },
  {
    n: "8",
    title: "Data Sharing",
    body: [
      "Swingle does not sell, rent or trade personal information to third parties.",
      "We may share information with trusted third-party service providers who help us operate the platform, including:",
      {
        list: [
          "Payment processors",
          "Cloud hosting providers",
          "Email delivery providers",
          "Analytics providers",
          "Customer support providers",
          "Fraud prevention services",
        ],
      },
      "These providers only receive the information necessary to perform their services and are contractually required to protect personal information.",
      "We may also disclose personal information where required by law or where necessary to protect our legal rights or prevent fraud.",
    ],
  },
  {
    n: "9",
    title: "Customer Data Ownership",
    body: [
      "Customer information collected by a Venue through Swingle remains under the control of that Venue.",
      "Swingle does not acquire ownership of customer data processed on behalf of Venues.",
      "We process customer information only for the purposes of:",
      {
        list: [
          "providing our services;",
          "maintaining platform functionality;",
          "ensuring platform security;",
          "preventing fraud;",
          "complying with legal obligations; and",
          "carrying out processing instructed by the relevant Venue.",
        ],
      },
    ],
  },
  {
    n: "10",
    title: "Data Security",
    body: [
      "We implement appropriate technical and organisational measures to protect personal information against accidental loss, misuse, unauthorised access, disclosure or destruction.",
      "These measures include:",
      {
        list: [
          "Encryption where appropriate",
          "Secure cloud hosting",
          "Access controls",
          "Security monitoring",
          "Secure payment processing",
          "Regular software updates",
        ],
      },
      "Although we take reasonable steps to protect information, no online service can guarantee absolute security.",
    ],
  },
  {
    n: "11",
    title: "Data Retention",
    body: [
      "We retain personal information only for as long as necessary to:",
      {
        list: [
          "provide our services;",
          "comply with legal obligations;",
          "resolve disputes;",
          "enforce our agreements; and",
          "meet legitimate business requirements.",
        ],
      },
      "Each Venue is responsible for determining how long customer information is retained within its Swingle account, subject to applicable legal requirements.",
    ],
  },
  {
    n: "12",
    title: "International Transfers",
    body: [
      "Where personal information is transferred outside the United Kingdom or European Economic Area, Swingle will ensure appropriate safeguards are in place, including the use of Standard Contractual Clauses or other recognised legal transfer mechanisms where required.",
    ],
  },
  {
    n: "13",
    title: "Your Rights",
    body: [
      "Depending on applicable law, you may have the right to:",
      {
        list: [
          "Access your personal information",
          "Correct inaccurate information",
          "Request deletion of your information",
          "Restrict processing",
          "Object to processing",
          "Request data portability",
          "Withdraw consent where processing is based upon consent",
        ],
      },
      "If your request relates to information held by a Venue using Swingle, you should normally contact that Venue directly.",
      "Where appropriate, Swingle will assist Venues in responding to requests concerning personal information processed through the platform.",
    ],
  },
  {
    n: "14",
    title: "Data Processing Agreement",
    body: [
      'Where required by applicable law, Swingle enters into a Data Processing Agreement ("DPA") with Venues.',
      "The DPA governs how Swingle processes customer information on behalf of Venues and ensures that appropriate contractual safeguards are in place to protect personal information in accordance with applicable data protection legislation.",
    ],
  },
  {
    n: "15",
    title: "Cookies",
    body: [
      "Swingle uses cookies and similar technologies to:",
      {
        list: [
          "Keep users signed in",
          "Maintain platform security",
          "Remember preferences",
          "Analyse website usage",
          "Improve platform performance",
        ],
      },
      "Where required by law, we will obtain consent before placing non-essential cookies.",
    ],
  },
  {
    n: "16",
    title: "Children's Privacy",
    body: [
      "Swingle is intended for use by individuals aged 18 or over, or those otherwise legally permitted to purchase tickets or memberships for events.",
      "We do not knowingly collect personal information from children where prohibited by law.",
    ],
  },
  {
    n: "17",
    title: "Changes to This Privacy Policy",
    body: [
      "We may update this Privacy Policy from time to time.",
      "Any changes will be published on our website, together with an updated Effective Date.",
      "Where required by law, we will notify users of significant changes.",
    ],
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-black text-[#F5F5F0]" data-testid="privacy-page">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" data-testid="privacy-home-link" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Swingle" className="h-8 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-6">Legal</p>
          <h1
            data-testid="privacy-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
          >
            Privacy
            <br />
            <span className="italic text-[#D9A6F5]">Policy</span>
          </h1>
          <p className="mt-8 text-sm uppercase tracking-[0.18em] text-[#A3A3A3]">
            Effective Date: 14th July 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-14">
          {sections.map((s) => (
            <article
              key={s.n}
              data-testid={`privacy-section-${s.n}`}
              className="grid md:grid-cols-[80px_1fr] gap-4 md:gap-10"
            >
              <div className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]/70 pt-1">
                {s.n.padStart(2, "0")}
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-normal text-[#F5F5F0] mb-4">
                  {s.title}
                </h2>
                <div className="space-y-4 text-sm md:text-base text-[#C9C9C0] leading-relaxed">
                  {s.body.map((item, i) => {
                    if (typeof item === "string") return <p key={i}>{item}</p>;
                    if (item.subheading)
                      return (
                        <h3
                          key={i}
                          className="text-sm uppercase tracking-[0.18em] text-[#D9A6F5] pt-2"
                        >
                          {item.subheading}
                        </h3>
                      );
                    return (
                      <ul key={i} className="list-none space-y-2 pl-0">
                        {item.list.map((li, j) => (
                          <li key={j} className="flex gap-3">
                            <span className="text-[#D9A6F5] mt-2 inline-block h-px w-3 flex-shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })}
                </div>
              </div>
            </article>
          ))}

          {/* Contact section */}
          <article
            data-testid="privacy-section-18"
            className="grid md:grid-cols-[80px_1fr] gap-4 md:gap-10 pt-4 border-t border-white/10"
          >
            <div className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]/70 pt-8">18</div>
            <div className="pt-8">
              <h2 className="text-lg md:text-xl font-normal text-[#F5F5F0] mb-4">Contact Us</h2>
              <div className="space-y-3 text-sm md:text-base text-[#C9C9C0] leading-relaxed">
                <p>
                  If you have any questions about this Privacy Policy or how personal information
                  is handled, please contact us.
                </p>
                <p className="pt-2">
                  <span className="block text-[#F5F5F0]">Swingle</span>
                  <span className="block">
                    Email:{" "}
                    <a
                      href="mailto:privacy@swingleapp.com"
                      className="text-[#D9A6F5] hover:underline"
                      data-testid="privacy-contact-email"
                    >
                      privacy@swingleapp.com
                    </a>
                  </span>
                  <span className="block">
                    Website:{" "}
                    <a
                      href="https://www.swingleforvenues.com"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#D9A6F5] hover:underline"
                    >
                      www.swingleforvenues.com
                    </a>
                  </span>
                </p>
                <p>
                  If your enquiry relates to a ticket purchase, membership or marketing
                  communication from a Venue, please include the name of the relevant Venue so we
                  can assist you or direct your enquiry appropriately.
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#A3A3A3] uppercase tracking-[0.18em]">
          <p>© {new Date().getFullYear()} Swingle For Venues.</p>
          <div className="flex items-center gap-5">
            <Link
              to="/terms"
              data-testid="privacy-footer-terms-link"
              className="text-[#A3A3A3]/80 hover:text-[#D9A6F5] transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/"
              data-testid="privacy-footer-home-link"
              className="text-[#A3A3A3]/70 hover:text-[#D9A6F5] transition-colors"
            >
              Return home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
